import { useState } from 'react';
import type { Player } from '../lib/types';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Bevestig,
  Foutmelding,
  Kaart,
  Knop,
  Leeg,
  Modaal,
  PaginaKop,
  useToast
} from '../components/ui';
import { useAuth } from '../lib/auth';
import { naamVan, rondeNaam, spelersVan, zetDoor } from '../lib/bracket';
import { datumVeld, fmtDatum, fmtDatumTijd, tijdVeld, toonNaam } from '../lib/format';
import { useDeleteTournament, useLeague, useRecordMatch, useSaveTournament } from '../lib/queries';
import { IcoonBeker } from '../components/icons';
import { usePoule } from '../lib/poule';
import type { Tie, Tournament } from '../lib/types';
import { valideerPotje } from '../lib/validate';

export default function ToernooiDetail() {
  const { pouleId } = usePoule();
  const { id = '' } = useParams();
  const navigeer = useNavigate();
  const { tournaments, matches, zoekSpeler } = useLeague(pouleId);
  const { isAdmin } = useAuth();
  const [partij, setPartij] = useState<{ ronde: number; index: number } | null>(null);
  const [weggooien, setWeggooien] = useState(false);
  const verwijderen = useDeleteTournament();
  const meld = useToast();

  const toernooi = tournaments.find((t) => t.id === id);

  if (!toernooi) {
    return (
      <>
        <PaginaKop titel="Toernooi" />
        <Kaart>
          <Leeg tekst="Dit toernooi bestaat niet (meer)." ikoon="beker" />
        </Kaart>
      </>
    );
  }

  async function gooiWeg(t: Tournament) {
    try {
      await verwijderen.mutateAsync({ toernooi: t, potjes: matches.filter((m) => m.tournamentId === t.id) });
      meld('Toernooi verwijderd.');
      navigeer('/toernooi');
    } catch (err) {
      meld(err instanceof Error ? err.message : 'Verwijderen mislukte.', true);
    }
    setWeggooien(false);
  }

  return (
    <>
      <PaginaKop
        titel={toernooi.name}
        onder={`${fmtDatum(toernooi.createdAt)} · ${toernooi.participants.length} deelnemers · ${
          toernooi.status === 'klaar' ? 'afgerond' : 'nog bezig'
        }`}
        rechts={
          <>
            <Knop onClick={() => navigeer('/toernooi')}>← Alle toernooien</Knop>
            {isAdmin ? (
              <Knop variant="gevaar" onClick={() => setWeggooien(true)}>
                Verwijderen
              </Knop>
            ) : null}
          </>
        }
      />

      {toernooi.championId ? (
        <div className="mb-3.5 flex items-center gap-3 rounded-kaart border border-amber-200 bg-goud-zacht px-4 py-3.5">
          <IcoonBeker className="!h-8 !w-8 text-goud" />
          <div>
            <div className="text-xs uppercase tracking-wide text-krijt-500">Winnaar</div>
            <h2 className="text-[17px] font-semibold">
              {naamVan(toernooi, toernooi.championId, (id) => toonNaam(zoekSpeler(id)))}
            </h2>
          </div>
        </div>
      ) : null}

      <Kaart>
        <div className="flex gap-6 overflow-x-auto pb-2">
          {toernooi.bracket.map((ronde, ri) => (
            <div key={ri} className="flex min-w-[218px] flex-col justify-around gap-3.5">
              <div className="text-[11.5px] font-semibold uppercase tracking-wide text-krijt-400">
                {rondeNaam(ri, toernooi.bracket.length)}
              </div>
              {ronde.map((p, pi) => (
                <PartijKaart
                  key={p.id}
                  partij={p}
                  eersteRonde={ri === 0}
                  potje={p.matchId ? matches.find((m) => m.id === p.matchId) : undefined}
                  deelnemers={(sleutel) => spelersVan(toernooi, sleutel).map(zoekSpeler)}
                  speelbaar={Boolean(p.a && p.b && !p.winnerId)}
                  onSpeel={() => setPartij({ ronde: ri, index: pi })}
                />
              ))}
            </div>
          ))}
        </div>
      </Kaart>

      <p className="mt-3 text-xs text-krijt-500">
        Toernooipotjes tellen gewoon mee voor de ranglijst; ze zijn in de historie herkenbaar aan het bekertje.
      </p>

      {partij ? (
        <PartijModaal
          toernooi={toernooi}
          ronde={partij.ronde}
          index={partij.index}
          onSluit={() => setPartij(null)}
        />
      ) : null}

      {weggooien ? (
        <Bevestig
          titel="Toernooi verwijderen?"
          tekst="Het schema en de bijbehorende toernooipotjes worden gewist. De ratings worden opnieuw doorgerekend."
          knop="Verwijderen"
          onJa={() => void gooiWeg(toernooi)}
          onNee={() => setWeggooien(false)}
        />
      ) : null}
    </>
  );
}

function PartijKaart({
  partij,
  eersteRonde,
  potje,
  deelnemers,
  speelbaar,
  onSpeel
}: {
  partij: Tie;
  eersteRonde: boolean;
  potje?: { scoreA: number; scoreB: number; playedAt: string };
  /** Eén speler bij 1v1, twee bij een duotoernooi. */
  deelnemers: (sleutel: string) => Player[];
  speelbaar: boolean;
  onSpeel: () => void;
}) {
  const zijde = (sleutel: string | null, isA: boolean) => {
    if (!sleutel) {
      return (
        <div className="flex items-center gap-2 px-2.5 py-1.5 text-[13.5px] italic text-krijt-400">
          {eersteRonde ? 'bye' : 'wacht op winnaar'}
        </div>
      );
    }
    const spelers = deelnemers(sleutel);
    const gewonnen = partij.winnerId === sleutel;
    return (
      <div
        className={`flex items-center gap-2 px-2.5 py-1.5 text-[13.5px] ${gewonnen ? 'bg-veld-50 font-semibold' : ''}`}
      >
        <span className="flex shrink-0 -space-x-1.5">
          {spelers.map((s) => (
            <Avatar key={s.id} speler={s} maat="sm" />
          ))}
        </span>
        <span className="truncate">{spelers.map(toonNaam).join(' & ')}</span>
        <span className="nums ml-auto font-semibold">{potje ? (isA ? potje.scoreA : potje.scoreB) : ''}</span>
      </div>
    );
  };

  return (
    <div className={`overflow-hidden rounded-md border bg-white ${speelbaar ? 'border-veld-500' : 'border-krijt-200'}`}>
      {zijde(partij.a, true)}
      <div className="border-t border-krijt-200">{zijde(partij.b, false)}</div>
      {speelbaar ? (
        <div className="border-t border-krijt-200 bg-krijt-50 px-2.5 py-1.5">
          <Knop klein variant="primair" onClick={onSpeel}>
            Uitslag invoeren
          </Knop>
        </div>
      ) : null}
      {potje ? (
        <div className="border-t border-krijt-200 bg-krijt-50 px-2.5 py-1.5 text-xs text-krijt-500">
          {fmtDatumTijd(potje.playedAt)}
        </div>
      ) : null}
    </div>
  );
}

function PartijModaal({
  toernooi,
  ronde,
  index,
  onSluit
}: {
  toernooi: Tournament;
  ronde: number;
  index: number;
  onSluit: () => void;
}) {
  const partij = toernooi.bracket[ronde][index];
  const { zoekSpeler } = useLeague(toernooi.pouleId);
  const vastleggen = useRecordMatch();
  const bewaren = useSaveTournament();
  const meld = useToast();

  const kantA = spelersVan(toernooi, partij.a);
  const kantB = spelersVan(toernooi, partij.b);
  const naamA = kantA.map((id) => toonNaam(zoekSpeler(id))).join(' & ');
  const naamB = kantB.map((id) => toonNaam(zoekSpeler(id))).join(' & ');
  const nu = new Date();

  const [scoreA, setScoreA] = useState('');
  const [scoreB, setScoreB] = useState('');
  const [datum, setDatum] = useState(datumVeld(nu));
  const [tijd, setTijd] = useState(tijdVeld(nu));
  const [fout, setFout] = useState<string | null>(null);
  const bezig = vastleggen.isPending || bewaren.isPending;

  async function bewaar() {
    /* De opstelling volgt de ploegenlijst: de eerste naam staat achterin. */
    const uitkomst = valideerPotje({
      pouleId: toernooi.pouleId,
      mode: toernooi.mode,
      a1: kantA[0] ?? '',
      a2: kantA[1] ?? '',
      b1: kantB[0] ?? '',
      b2: kantB[1] ?? '',
      scoreA,
      scoreB,
      datum,
      tijd,
      tournamentId: toernooi.id
    });
    if (!uitkomst.ok) {
      setFout(uitkomst.fout);
      return;
    }

    try {
      const potje = await vastleggen.mutateAsync(uitkomst.potje);
      const winnaar = uitkomst.potje.scoreA > uitkomst.potje.scoreB ? partij.a : partij.b;
      const bijgewerkt = zetDoor(toernooi, ronde, index, winnaar as string, potje.id);
      await bewaren.mutateAsync(bijgewerkt);
      meld(
        bijgewerkt.status === 'klaar'
          ? `${naamVan(bijgewerkt, bijgewerkt.championId, (id) => toonNaam(zoekSpeler(id)))} wint het toernooi!`
          : 'Uitslag vastgelegd.'
      );
      onSluit();
    } catch (err) {
      setFout(err instanceof Error ? err.message : 'Opslaan mislukte.');
    }
  }

  return (
    <Modaal
      titel={`${rondeNaam(ronde, toernooi.bracket.length)}: ${naamA} – ${naamB}`}
      onSluit={onSluit}
      voet={
        <>
          <Knop onClick={onSluit}>Annuleren</Knop>
          <Knop variant="primair" onClick={() => void bewaar()} disabled={bezig}>
            {bezig ? 'Bezig…' : 'Vastleggen'}
          </Knop>
        </>
      }
    >
      {fout ? <Foutmelding>{fout}</Foutmelding> : null}

      <div className="my-3 flex items-center justify-center gap-5">
        <div className="text-center">
          <span className="flex justify-center -space-x-2">
            {kantA.map((id) => (
              <Avatar key={id} speler={zoekSpeler(id)} maat="lg" />
            ))}
          </span>
          <div className="mt-1.5 text-sm">{naamA}</div>
          <input
            type="number"
            min={0}
            max={10}
            inputMode="numeric"
            className="veld nums mt-1 w-20 text-center text-xl font-semibold"
            value={scoreA}
            onChange={(e) => setScoreA(e.target.value)}
          />
        </div>
        <span className="text-krijt-400">tegen</span>
        <div className="text-center">
          <span className="flex justify-center -space-x-2">
            {kantB.map((id) => (
              <Avatar key={id} speler={zoekSpeler(id)} maat="lg" />
            ))}
          </span>
          <div className="mt-1.5 text-sm">{naamB}</div>
          <input
            type="number"
            min={0}
            max={10}
            inputMode="numeric"
            className="veld nums mt-1 w-20 text-center text-xl font-semibold"
            value={scoreB}
            onChange={(e) => setScoreB(e.target.value)}
          />
        </div>
      </div>

      <div className="flex justify-center gap-2">
        <input type="date" className="veld w-auto" value={datum} onChange={(e) => setDatum(e.target.value)} />
        <input type="time" className="veld w-auto" value={tijd} onChange={(e) => setTijd(e.target.value)} />
      </div>
    </Modaal>
  );
}
